/**
 * PageHead — the house page header (`record.html` / `forms.html` `.phead`;
 * WEB TIER A · WT4).
 *
 * The approved mockups' page-top grammar: an `H1/24` title with an optional
 * one-line sub directly under it, and an actions cluster on the right. Born in
 * WT4 with the rest of the record vocabulary (plan §2: "build once, in
 * `components/ui/`"); its first page consumers are the Tier A write surfaces
 * and Settings (WT5–WT7) — the QUOTE RECORD itself deliberately does not use
 * it, because `record.html`'s ruling is that the record's identity line lives
 * INSIDE a card ("on the page canvas text/secondary measures 4.10; on
 * bg/surface it is 4.64 — every quiet line in this file is inside a card").
 *
 * THE SUB-LINE IS `text/primary`, NOT `text/secondary` — D3's named cost,
 * paid in full (the mockup's own comment on `.psub`): this header sits on the
 * authored canvas, where the quiet token fails AA. Nothing quiet sits on the
 * canvas.
 *
 * `components/shell/AppShell.tsx`'s `PageHeader` is the PRE-Tier-A shell
 * furniture and survives on the pages Tier A does not rebuild; the two coexist
 * until the Tier B register migration retires the old one (ledger item 6).
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface PageHeadProps {
  title: string;
  /** One line under the title. Rendered `text-fg` — the canvas rule above. */
  sub?: ReactNode;
  /** Right-aligned actions cluster. */
  actions?: ReactNode;
  className?: string;
}

export function PageHead({ title, sub, actions, className }: PageHeadProps): React.JSX.Element {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h1 className="text-h1 text-fg">{title}</h1>
        {sub === undefined ? null : <p className="mt-0.5 text-label text-fg">{sub}</p>}
      </div>
      {actions === undefined ? null : (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </header>
  );
}
